import { trackGenericEvent } from "@utils/api/tracker";
import { errorConstants } from "@utils/services/PurchaseErrorMessages";

/**
 * Payment type constants
 */
export const paymentTypes = {
  CREDIT_CARD: "CREDIT_CARD",
  PAYPAL: "PAYPAL",
};

class Braintree {
  constructor(country_code) {
    this.hostedFieldsInstance = null;
    this.paypalInstance = null;
    this.paypalNonce = null;
    this.inputFieldsFontSize = country_code === "US" ? "16px" : "18px";

    this.setupBraintreeClient = this.setupBraintreeClient.bind(this);
  }

  /**
   * Set up braintree
   */
  setupBraintreeClient(langPlaceholders) {
    const placeholders = Object.assign(
      {
        CVV: "3 Digits",
        EXP: "MM/YY",
      },
      langPlaceholders
    );
    return new Promise((resolve, reject) => {
      window.braintree.client
        .create({
          authorization: __BRAINTREE_TOKENIZATION_KEY__,
        })
        .then((client) =>
          Promise.all([
            this.setupHostedFieldsInstance(client, placeholders),
            this.setupPayPalInstance(client),
          ])
        )
        .then(resolve)
        .catch(reject);
    });
  }

  /**
   * Initialize the braintree client
   */
  setupHostedFieldsInstance(client, placeholders) {
    return new Promise((resolve, reject) => {
      const fields = {
        number: {
          selector: "#cardNumberWrapper",
        },
        cvv: {
          selector: "#cardCvvWrapper",
          placeholder: placeholders.CVV || "3 Digits",
        },
        expirationDate: {
          selector: "#cardExpirationDateWrapper",
          placeholder: placeholders.EXP || "MM/YY",
        },
      };

      window.braintree.hostedFields
        .create({
          client,
          fields,
          styles: {
            input: {
              "font-size": this.inputFieldsFontSize,
              color: "#404040",
              padding: "12px 0",
            },
          },
        })
        .then((instance) => {
          this.hostedFieldsInstance = instance;
          this.hostedFieldsInstance.on("cardTypeChange", (e) => {
            if (e.cards && e.cards.length == 1) {
              const cardType = e.cards[0].type;
              $("#cardNumberWrapper").removeClass(
                "visa american-express master-card"
              );
              $("#cardNumberWrapper").addClass(cardType);
            }
          });
          resolve();
        })
        .catch(reject);
    });
  }

  /**
   * Initialize the braintree paypal client
   */
  setupPayPalInstance(client) {
    return new Promise((resolve, reject) => {
      window.braintree.paypal
        .create({
          client,
        })
        .then((instance) => {
          this.paypalInstance = instance;
          resolve(instance);
        })
        .catch(reject);
    });
  }

  /**
   * Get payment nonce from paypal.
   */
  tokenizePayPal() {
    return new Promise((resolve, reject) => {
      const configs = {
        flow: "vault",
      };
      this.paypalInstance.tokenize(configs, (tokenizeErr, payload) => {
        if (tokenizeErr) {
          reject(tokenizeErr);
        } else {
          this.paypalNonce = payload.nonce;
          resolve({
            paymentType: paymentTypes.PAYPAL,
            details: payload.details,
          });
        }
      });
    });
  }

  /**
   * Tokenize the credit card info to generate a payment nonce.
   */
  tokenizeCreditCard() {
    return new Promise((resolve, reject) => {
      this.hostedFieldsInstance.tokenize((tokenizeErr, payload) => {
        if (tokenizeErr) {
          let errorMessage;
          switch (tokenizeErr.code) {
            case errorConstants.hostedFieldsFieldsEmpty:
              // occurs when none of the fields are filled in
              errorMessage = "All fields are empty! Please fill out the form.";
              break;
            case errorConstants.hostedFieldsFieldsInvalid:
              // occurs when certain fields do not pass client side validation
              errorMessage = `Some fields are invalid: ${tokenizeErr.details.invalidFieldKeys}`;
              break;
            case errorConstants.hostedFieldsTokenizationFailOnDuplicate:
              // occurs when:
              //   * the client token used for client authorization was generated
              //     with a customer ID and the fail on duplicate payment method
              //     option is set to true
              //   * the card being tokenized has previously been vaulted (with any customer)
              // See: https://developers.braintreepayments.com/reference/request/client-token/generate/#options.fail_on_duplicate_payment_method
              errorMessage =
                "This payment method already exists in your vault.";
              break;
            case errorConstants.hostedFieldsTokenizationCVVVerificationFailed:
              // occurs when:
              //   * the client token used for client authorization was generated
              //     with a customer ID and the verify card option is set to true
              //     and you have credit card verification turned on in the Braintree
              //     control panel
              //   * the cvv does not pass verfication (https://developers.braintreepayments.com/reference/general/testing/#avs-and-cvv/cid-responses)
              // See: https://developers.braintreepayments.com/reference/request/client-token/generate/#options.verify_card
              errorMessage = "CVV did not pass verification";
              break;
            case errorConstants.hostedFieldsFailedTokenization:
              // occurs for any other tokenization error on the server
              errorMessage =
                "Tokenization failed server side. Is the card valid?";
              break;
            case errorConstants.hostedFieldsTokenizationNetworkError:
              // occurs when the Braintree gateway cannot be contacted
              errorMessage = "Network error occurred when tokenizing.";
              break;
            default:
              errorMessage = "Something bad happened!";
          }

          trackGenericEvent("FailedToTokenizeCreditCard", {
            errorCode: tokenizeErr.code,
            errorMessage,
          });

          if (errorMessage) console.error(errorMessage);

          reject(tokenizeErr);
        } else {
          resolve(payload.nonce);
        }
      });
    });
  }
}

export default Braintree;
